import brandingTokens from '../branding';
import { endpoint } from '../helpers/Endpoints';
import { getDataLayer, setDataLayer } from '../helpers/GoogleTagManager';
import { key } from '../helpers/LocalStorage';
import { route } from '../helpers/Routes';
import { setSession } from '../helpers/Session';
import * as FetchAction from './FetchAction';

export const SIGNING_IN = '@la:SIGNING_IN';
export const SIGNING_IN_SUCCESS = '@la:SIGNING_IN_SUCCESS';
export const SIGNING_IN_ERROR = '@la:SIGNING_IN_ERROR';
export const SIGNING_IN_UNKNOWN_ERROR = '@la:SIGNING_IN_UNKNOWN_ERROR';

export const SINGLE_SIGN_ON = '@la:SINGLE_SIGN_ON';
export const SINGLE_SIGN_ON_SUCCESS = '@la:SINGLE_SIGN_ON_SUCCESS';
export const SINGLE_SIGN_ON_ERROR = '@la:SINGLE_SIGN_ON_ERROR';
export const SINGLE_SIGN_ON_UNKNOWN_ERROR = '@la:SINGLE_SIGN_ON_UNKNOWN_ERROR';

export function signIn(username: string, password: string) {
	const params = {
		username,
		password,
	};

	const postBodyEncoded = Object.keys(params)
		.map((paramKey) => {
			return (
				encodeURIComponent(paramKey) +
				'=' +
				encodeURIComponent(params[paramKey])
			);
		})
		.join('&');

	const headers = new Headers();
	headers.append(
		'Content-Type',
		'application/x-www-form-urlencoded;charset=UTF-8'
	);

	return FetchAction.CreateFetchAction({
		startAction: SIGNING_IN,
		url: endpoint.token(),
		method: 'POST',
		headers,
		body: postBodyEncoded,
		onAfterSuccess: (payload, dispatch) => {
			if (!payload) {
				dispatch({ type: SIGNING_IN_UNKNOWN_ERROR });
				return;
			}
			setLocalStorage(payload);
			setAnalyticsDataLayer(payload);

			const returnUrl = getParameterByName('ReturnUrl');

			const returnUrlIsInvalid =
				returnUrl?.includes(route.logout()) ||
				returnUrl?.includes(endpoint.logout) ||
				returnUrl?.includes(endpoint.login) ||
				!returnUrl;

			window.location.href = returnUrlIsInvalid ? '/' : returnUrl;
		},
		onFailure: (_, dispatch) => {
			dispatch({ type: SIGNING_IN_ERROR });
			dispatch({ type: SIGNING_IN_UNKNOWN_ERROR });
		},
	});
}

export function singleSignOn(
	externalId: string,
	siteType: string,
	ticks: string,
	token: string,
	redirectUri: string
) {
	const body = JSON.stringify({
		externalId,
		siteType,
		ticks,
		token,
	});

	return FetchAction.CreateFetchAction({
		startAction: SINGLE_SIGN_ON,
		url: endpoint.singleSignOn(),
		method: 'POST',
		redirectOnUnauthorized: true,
		unauthorizedUrl: '/singleSignOn/unauthorized',
		body,
		onAfterSuccess: (payload, dispatch) => {
			if (!payload) {
				dispatch({ type: SINGLE_SIGN_ON_UNKNOWN_ERROR });

				return;
			}
			setLocalStorage(payload);
			setAnalyticsDataLayer(payload);
			if (redirectUri?.startsWith('/')) {
				window.location.href = redirectUri;
			} else {
				window.location.href = '/';
			}
		},
		onFailure: (valueResponse, dispatch) => {
			dispatch({ type: SINGLE_SIGN_ON_ERROR });
			dispatch({ type: SINGLE_SIGN_ON_UNKNOWN_ERROR });
		},
	});
}

export function getParameterByName(name, decode = true) {
	const match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);

	return (
		match &&
		(decode ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : match[1])
	);
}

function setLocalStorage(payload) {
	window.localStorage.setItem(key.customerName, payload.customerName);
	window.localStorage.setItem(key.customerId, payload.customerId);
	window.localStorage.setItem(key.externalId, payload?.externalId ?? '');
	window.localStorage.setItem(
		key.customerAttributes,
		JSON.stringify(payload.customerAttributes)
	);
	window.localStorage.setItem(
		key.expiresAt,
		'' + (new Date().getTime() + payload.clientExpiresIn)
	);
	window.localStorage.setItem(key.expiresIn, payload.clientExpiresIn);
	window.localStorage.setItem(key.roles, JSON.stringify(payload.roles));
	window.localStorage.setItem(
		key.preferences,
		JSON.stringify(payload.preferences)
	);
	window.localStorage.setItem(key.userId, payload.userId);
	window.localStorage.setItem(key.customerCount, payload.customerCount);
	window.localStorage.setItem(key.isSingleSignOn, payload.isSingleSignOn);
	window.localStorage.setItem(key.userProfileId, payload.userProfile.id);
	setSession({
		...payload,
		expiresAt: '' + (new Date().getTime() + payload.clientExpiresIn),
	});
}

export function setAnalyticsDataLayer(payload) {
	const { userId, customerId, userProfile, externalId } = payload;

	const isExternalDssi = brandingTokens.application === 'dssi' && !!externalId;
	const user_id = isExternalDssi ? `DSSI.${externalId}` : `OGM.${userId}`;

	setDataLayer({
		...getDataLayer(),
		user_id,
		user_corp: customerId,
		user_profile: userProfile.description,
	});
}
